// packages/common/log/src/config.ts
var LogLevel;
(function(LogLevel2) {
  LogLevel2[LogLevel2["TRACE"] = -1] = "TRACE";
  LogLevel2[LogLevel2["DEBUG"] = 0] = "DEBUG";
  LogLevel2[LogLevel2["INFO"] = 1] = "INFO";
  LogLevel2[LogLevel2["WARN"] = 2] = "WARN";
  LogLevel2[LogLevel2["ERROR"] = 3] = "ERROR";
})(LogLevel || (LogLevel = {}));
var levels = {
  trace: LogLevel.TRACE,
  debug: LogLevel.DEBUG,
  info: LogLevel.INFO,
  warn: LogLevel.WARN,
  error: LogLevel.ERROR
};
var shortLevelName = {
  [LogLevel.TRACE]: "T",
  [LogLevel.DEBUG]: "D",
  [LogLevel.INFO]: "I",
  [LogLevel.WARN]: "W",
  [LogLevel.ERROR]: "E"
};
var LogProcessorType;
(function(LogProcessorType2) {
  LogProcessorType2["CONSOLE"] = "console";
  LogProcessorType2["BROWSER"] = "browser";
  LogProcessorType2["DEBUG"] = "debug";
})(LogProcessorType || (LogProcessorType = {}));

// packages/common/log/src/scope.ts
var logInfoProperties = Symbol("logInfoProperties");
var logInfo = (target, propertyKey, descriptor) => {
  var _a2;
  ((_a2 = target[logInfoProperties]) != null ? _a2 : target[logInfoProperties] = []).push(propertyKey);
};
var gatherLogInfoFromScope = (scope) => {
  var _a2;
  if (!scope) {
    return {};
  }
  const res = {};
  const prototype = Object.getPrototypeOf(scope);
  const infoProps = (_a2 = prototype[logInfoProperties]) != null ? _a2 : [];
  for (const prop of infoProps) {
    try {
      res[prop] = typeof scope[prop] === "function" ? scope[prop]() : scope[prop];
    } catch (err) {
      res[prop] = err.message;
    }
  }
  return res;
};

// packages/common/log/src/context.ts
var matchFilter = (filter, level, path) => {
  return level >= filter.level && (!filter.pattern || path.includes(filter.pattern));
};
var shouldLog = (config, level, path) => {
  if (config.filters === void 0) {
    return true;
  } else {
    return config.filters.some((filter) => matchFilter(filter, level, path));
  }
};
var getContextFromEntry = (entry) => {
  let context;
  if (entry.meta) {
    const scopeInfo = gatherLogInfoFromScope(entry.meta.scope);
    if (Object.keys(scopeInfo).length > 0) {
      context = Object.assign(context != null ? context : {}, scopeInfo);
    }
  }
  if (entry.context) {
    if (entry.context instanceof Error) {
      const c = entry.context.context;
      context = Object.assign(context != null ? context : {}, {
        error: entry.context.stack,
        ...c
      });
    } else if (typeof entry.context === "object") {
      context = Object.assign(context != null ? context : {}, entry.context);
    }
  }
  return context && Object.keys(context).length > 0 ? context : void 0;
};

// packages/common/log/src/options.ts
import defaultsDeep from "lodash.defaultsdeep";

// packages/common/log/src/platform/browser/index.ts
var loadOptions = (filepath) => {
  try {
    return JSON.parse(localStorage.getItem("dxlog"));
  } catch (err) {
    return void 0;
  }
};

// packages/common/log/src/processors/console-processor.ts
import chalk from "chalk";
import pickBy from "lodash.pickby";
import { inspect } from "@dxos/node-std/util";
var LEVEL_COLORS = {
  [LogLevel.TRACE]: "gray",
  [LogLevel.DEBUG]: "gray",
  [LogLevel.INFO]: "white",
  [LogLevel.WARN]: "yellow",
  [LogLevel.ERROR]: "red"
};
var truncate = (text, length = 0, right = false) => {
  const str = text && length ? right ? text.slice(-length) : text.substring(0, length) : text != null ? text : "";
  return right ? str.padStart(length, " ") : str.padEnd(length, " ");
};
var getRelativeFilename = (filename) => {
  const match = filename.match(/.+\/(packages\/.+\/.+)/);
  if (match) {
    const [, filePath] = match;
    return filePath;
  }
  return filename;
};
var DEFAULT_FORMATTER = (config, { path, line, level, message, context, error }) => {
  var _a2, _b;
  const column = (_b = (_a2 = config.options) == null ? void 0 : _a2.formatter) == null ? void 0 : _b.column;
  const filepath = path !== void 0 && line !== void 0 ? chalk.grey(`${path}:${line}`) : void 0;
  return [
    // NOTE: File path must come fist for console hyperlinks.
    // Must not truncate for terminal output.
    filepath,
    column && filepath ? "".padStart(column - filepath.length) : void 0,
    chalk[LEVEL_COLORS[level]](column ? shortLevelName[level] : LogLevel[level]),
    message,
    context,
    error
  ];
};
var SHORT_FORMATTER = (config, { path, level, message }) => [
  chalk.grey(truncate(path, 16, true)),
  chalk[LEVEL_COLORS[level]](shortLevelName[level]),
  message
];
var formatter = DEFAULT_FORMATTER;
var CONSOLE_PROCESSOR = (config, entry) => {
  var _a2;
  const { level, message, meta, error } = entry;
  if (!shouldLog(config, level, (_a2 = meta == null ? void 0 : meta.file) != null ? _a2 : "")) {
    return;
  }
  const parts = {
    level,
    message,
    error
  };
  if (meta) {
    parts.path = getRelativeFilename(meta.file);
    parts.line = meta.line;
  }
  const context = getContextFromEntry(entry);
  if (context) {
    parts.context = inspect(pickBy(context, (value) => value !== void 0), {
      depth: config.options.depth,
      colors: true,
      maxArrayLength: 8,
      sorted: false
    });
  }
  const line = formatter(config, parts).filter(Boolean).join(" ");
  console.log(line);
};

// packages/common/log/src/processors/debug-processor.ts
import { inspect as inspect2 } from "@dxos/node-std/util";
var DEBUG_PROCESSOR = (config, entry) => {
  console.log(inspect2(entry, false, null, true));
};

// packages/common/log/src/processors/browser-processor.ts
var getRelativeFilename2 = (filename) => {
  const match = filename.match(/.+\/(packages\/.+\/.+)/);
  if (match) {
    const [, filePath] = match;
    return filePath;
  }
  return filename;
};
var CONFIG = typeof mochaExecutor !== "undefined" ? {
  useTestProcessor: true,
  printFileLinks: true
} : {
  useTestProcessor: false,
  printFileLinks: false
};
var APP_BROWSER_PROCESSOR = (config, entry) => {
  var _a2, _b, _c, _d, _e;
  if (!shouldLog(config, entry.level, (_b = (_a2 = entry.meta) == null ? void 0 : _a2.file) != null ? _b : "")) {
    return;
  }
  const LOG_BROWSER_PREFIX = (_c = config.prefix) != null ? _c : "https://vscode.dev/github.com/dxos/dxos/blob/main/";
  const LOG_BROWSER_CSS = [];
  let link = "";
  if (entry.meta) {
    const filename = getRelativeFilename2(entry.meta.file);
    const filepath = `${LOG_BROWSER_PREFIX.replace(/\/$/, "")}/${filename}`;
    link = `${filepath}#L${entry.meta.line}`;
  }
  let args = [];
  args.push(entry.message);
  const context = getContextFromEntry(entry);
  if (context) {
    args.push(context);
  }
  const levels2 = {
    [LogLevel.ERROR]: console.error,
    [LogLevel.WARN]: console.warn,
    [LogLevel.DEBUG]: console.log
  };
  if (CONFIG.printFileLinks) {
    if (LOG_BROWSER_CSS == null ? void 0 : LOG_BROWSER_CSS.length) {
      args = [
        `%c${link}
%c${args.join(" ")}`,
        ...LOG_BROWSER_CSS
      ];
    } else {
      args = [
        link + "\n",
        ...args
      ];
    }
  }
  const level = (_d = levels2[entry.level]) != null ? _d : console.log;
  if (typeof ((_e = entry.meta) == null ? void 0 : _e.callSite) === "function") {
    entry.meta.callSite(level, args);
  } else {
    level(...args);
  }
};
var TEST_BROWSER_PROCESSOR = (config, entry) => {
  var _a2, _b, _c, _d;
  if (!shouldLog(config, entry.level, (_b = (_a2 = entry.meta) == null ? void 0 : _a2.file) != null ? _b : "")) {
    return;
  }
  let path = "";
  if (entry.meta) {
    path = `${getRelativeFilename2(entry.meta.file)}:${entry.meta.line}`;
  }
  let args = [];
  args.push(entry.message);
  const context = getContextFromEntry(entry);
  if (context) {
    args.push(context);
  }
  const levels2 = {
    [LogLevel.ERROR]: console.error,
    [LogLevel.WARN]: console.warn,
    [LogLevel.DEBUG]: console.log
  };
  if (CONFIG.printFileLinks) {
    args = [
      path,
      ...args
    ];
  }
  const level = (_c = levels2[entry.level]) != null ? _c : console.log;
  if (typeof ((_d = entry.meta) == null ? void 0 : _d.callSite) === "function") {
    entry.meta.callSite(level, args);
  } else {
    level(...args);
  }
};
var BROWSER_PROCESSOR = CONFIG.useTestProcessor ? TEST_BROWSER_PROCESSOR : APP_BROWSER_PROCESSOR;

// packages/common/log/src/options.ts
var processors = {
  [LogProcessorType.CONSOLE]: CONSOLE_PROCESSOR,
  [LogProcessorType.BROWSER]: BROWSER_PROCESSOR,
  [LogProcessorType.DEBUG]: DEBUG_PROCESSOR
};
var parseFilter = (filter) => {
  if (typeof filter === "number") {
    return [
      {
        level: filter
      }
    ];
  }
  const parseLogLevel = (level, defValue = LogLevel.WARN) => {
    var _a2;
    return (_a2 = levels[level.toLowerCase()]) != null ? _a2 : defValue;
  };
  const lines = typeof filter === "string" ? filter.split(/,\s*/) : filter;
  return lines.map((filter2) => {
    const [pattern, level] = filter2.split(":");
    return level ? {
      level: parseLogLevel(level),
      pattern
    } : {
      level: parseLogLevel(pattern)
    };
  });
};
var IS_BROWSER = typeof window !== "undefined" || typeof navigator !== "undefined";
var getConfig = (_options) => {
  var _a2, _b, _c, _d;
  let options = defaultsDeep({}, _options, "process" in globalThis && {
    // TODO(burdon): Node only.
    file: (_a2 = process.env) == null ? void 0 : _a2.LOG_CONFIG,
    filter: (_b = process.env) == null ? void 0 : _b.LOG_FILTER,
    processor: (_c = process.env) == null ? void 0 : _c.LOG_PROCESSOR
  });
  options = defaultsDeep({}, loadOptions(options.file), options);
  const defaultProcessor = IS_BROWSER ? BROWSER_PROCESSOR : CONSOLE_PROCESSOR;
  return {
    options,
    filters: parseFilter((_d = options.filter) != null ? _d : LogLevel.INFO),
    processors: [
      options.processor ? processors[options.processor] : defaultProcessor
    ],
    prefix: options.prefix
  };
};

// packages/common/log/src/log.ts
var createLog = () => {
  const log2 = (...params) => processLog(LogLevel.DEBUG, ...params);
  log2._config = getConfig();
  Object.defineProperty(log2, "runtimeConfig", {
    get: () => log2._config
  });
  log2.config = (options) => {
    log2._config = getConfig(options);
  };
  log2.trace = (...params) => processLog(LogLevel.TRACE, ...params);
  log2.debug = (...params) => processLog(LogLevel.DEBUG, ...params);
  log2.info = (...params) => processLog(LogLevel.INFO, ...params);
  log2.warn = (...params) => processLog(LogLevel.WARN, ...params);
  log2.error = (...params) => processLog(LogLevel.ERROR, ...params);
  log2.catch = (error, context, meta) => processLog(LogLevel.ERROR, error.stack, context, meta);
  log2.break = () => log2.info("\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014\u2014");
  const processLog = (level, message, context, meta, error) => {
    log2._config.processors.forEach((processor) => processor(log2._config, {
      level,
      message,
      context,
      meta,
      error
    }));
  };
  return log2;
};
var _a;
var log = (_a = globalThis.dx_log) != null ? _a : globalThis.dx_log = createLog();

// packages/common/log/src/experimental/ownership.ts
import { inspect as inspect3 } from "@dxos/node-std/util";
var kOwnershipScope = Symbol("kOwnershipScope");
var kCurrentOwnershipScope = Symbol("kCurrentOwnershipScope");
var kDebugInfoProperties = Symbol("kDebugInfoProperties");
var OwnershipScope = class {
  constructor(constr, parent) {
    this.constr = constr;
    this.parent = parent;
  }
  getInfo() {
    var _a2;
    if (!this.instance) {
      return {};
    }
    const props = (_a2 = this.constr.prototype[kDebugInfoProperties]) != null ? _a2 : [];
    const info = {};
    for (const prop of props) {
      info[prop] = this.instance[prop];
    }
    return info;
  }
  [inspect3.custom]() {
    return {
      className: this.constr.name,
      info: this.getInfo(),
      parent: this.parent
    };
  }
};
var getCurrentOwnershipScope = (thisRef) => thisRef;
export {
  BROWSER_PROCESSOR,
  CONSOLE_PROCESSOR,
  DEBUG_PROCESSOR,
  DEFAULT_FORMATTER,
  LogLevel,
  LogProcessorType,
  SHORT_FORMATTER,
  gatherLogInfoFromScope,
  getContextFromEntry,
  getCurrentOwnershipScope,
  levels,
  log,
  logInfo,
  shortLevelName,
  shouldLog,
  truncate
};
//# sourceMappingURL=index.mjs.map
